import { Injectable } from '@angular/core';
import { SideMenuItemDto } from '@core/dtos/side-menu-items.dto';
import { ApiResponse } from '@core/interfaces/api/api-response.interface';
import { SideMenuGroup } from '@core/interfaces/entities/side-menu-group.interface';
import {
  SideMenuItem,
  SideMenuItemsResponse,
} from '@core/interfaces/entities/side-menu-items.interface';
import { SideMenuGroupMapper } from '@core/mappers/side-menu-group.mapper';
import { SideMenuItemsMapper } from '@core/mappers/side-menu-items.mapper';
import { ApiService } from '@core/services/api-service/api.service';
import { UserStateService } from '@core/services/user-state/user-state.service';
import { map, Observable, filter, switchMap } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { SideMenuGroupDto } from '@core/dtos/side-menu-group.dto';
import { ModulePermissionsDto } from '@core/dtos/profile.dto';
import { RESOURCES } from '@shared/constants/resources.constants';

@Injectable({
  providedIn: 'root',
})
export class SideMenuGroupService {
  private currentProfile$: Observable<any>;

  constructor(
    private apiService: ApiService,
    private sideMenuGroupMapper: SideMenuGroupMapper,
    private sideMenuItemsMapper: SideMenuItemsMapper,
    private userStateService: UserStateService
  ) {
    this.currentProfile$ = toObservable(this.userStateService.currentProfile);
  }

  private getReadPermissions(): string[] {
    const modulePermissions: ModulePermissionsDto =
      this.userStateService.getUserModulePermissions();
    return Object.entries(modulePermissions)
      .filter(([, modulePermission]) => modulePermission.permissions.Read)
      .map(([name]) => name);
  }

  private filterMenuItems(
    items: SideMenuItem[],
    isModule: boolean
  ): SideMenuItem[] {
    const permissions: string[] = this.getReadPermissions();

    return items.filter((item) => {
      //Si está oculto o inactivo, se descarta
      if (item.hidden || !item.active) return false;
      //Si no tiene subitems y no tiene url, se descarta
      if (!item.modules && !item.url) return false;

      //Si es un módulo y no tiene permisos, se descarta
      if (isModule && !permissions.includes(item.name)) return false;

      // Si tiene subitems, se filtran los subitems
      if (item.modules) {
        item.modules = this.filterMenuItems(item.modules, true);
        // Se mantiene el grupo solo si tiene al menos un subitem válido
        return item.modules.length > 0;
      }

      return true;
    });
  }

  getSideMenuItems(): Observable<ApiResponse<SideMenuItemDto[]>> {
    // Esperar hasta que el perfil del usuario esté disponible antes de proceder
    return this.currentProfile$.pipe(
      filter((profile) => !!profile), // Solo continuar cuando el perfil esté disponible
      switchMap(() =>
        this.apiService
          .get<ApiResponse<SideMenuItemsResponse>>(RESOURCES.SideMenuItems)
          .pipe(
            map((response) => {
              const { modules, side_menu_groups } = response.data;
              const filteredModules: SideMenuItem[] = this.filterMenuItems(
                modules,
                true
              );
              const filteredGroups: SideMenuItem[] = this.filterMenuItems(
                side_menu_groups,
                false
              );

              const mappedArrayModules: SideMenuItemDto[] =
                this.sideMenuItemsMapper.mapFromArray(filteredModules);
              const mappedArrayGroups: SideMenuItemDto[] =
                this.sideMenuItemsMapper.mapFromArray(filteredGroups);
              const mappedArray: SideMenuItemDto[] = [
                ...mappedArrayModules,
                ...mappedArrayGroups,
              ];
              this.userStateService.updateSideMenuItems(mappedArray);
              return {
                ...response,
                data: mappedArray,
              };
            })
          )
      )
    );
  }

  getSideMenuGroups() {
    return this.apiService
      .get<ApiResponse<{ [key: string]: SideMenuGroup[] }>>(
        RESOURCES.SideMenuGroups
      )
      .pipe(
        map((response) => {
          const mappedArray: SideMenuGroupDto[] =
            this.sideMenuGroupMapper.mapFromArray(
              response.data['side_menu_groups']
            );
          return {
            ...response,
            data: mappedArray,
          };
        })
      );
  }

  updateSideMenuGroup(sideMenuGroups: SideMenuGroupDto[]) {
    const mappedGroups: SideMenuGroup[] = this.sideMenuGroupMapper.mapToArray(sideMenuGroups);
    const payload: { side_menu_groups: SideMenuGroup[] } = {
      side_menu_groups: mappedGroups,
    };
    return this.apiService
      .put<ApiResponse<SideMenuGroup[]>>(RESOURCES.updateSideMenuGroup, payload)
      .pipe(
        map((response) => {
          return this.sideMenuGroupMapper.mapFromArray(response.data);
        })
      );
  }
}
